import React, { useState, useEffect, useMemo } from 'react'
import Swal from 'sweetalert2'
import Select from 'react-select'
import apiClient, { API_ENDPOINTS, BASE_URL } from '../../config/api'

const SetDiscounts = () => {
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    discount_type: 'percentage', // 'percentage' or 'fixed'
    discount_value: '',
    apply_to: 'all', // 'all', 'product', 'category', 'branch', 'min_purchase'
    product: '',
    category: '',
    branch: '',
    min_purchase_amount: '',
    max_discount_amount: '',
    start_date: '',
    end_date: '',
    usage_limit: '',
    is_active: true,
    description: ''
  })
  const [loading, setLoading] = useState(false)
  const [products, setProducts] = useState([])
  const [categories, setCategories] = useState([])
  const [branches, setBranches] = useState([])

  useEffect(() => {
    // Fetch products, categories, and branches for selection
    const fetchData = async () => {
      try {
        const [productsRes, categoriesRes, branchesRes] = await Promise.all([
          apiClient.get(`${BASE_URL}/api/products/products/`).catch(() => ({ data: [] })),
          apiClient.get(API_ENDPOINTS.CATEGORIES).catch(() => ({ data: [] })),
          apiClient.get(API_ENDPOINTS.BRANCHES).catch(() => ({ data: [] }))
        ])
        setProducts(productsRes.data?.results || productsRes.data || [])
        setCategories(categoriesRes.data?.results || categoriesRes.data || [])
        setBranches(branchesRes.data?.results || branchesRes.data || [])
      } catch (error) {
        console.error('Error fetching data:', error)
      }
    }
    fetchData()
  }, [])

  // Transform data for react-select
  const productOptions = useMemo(() => {
    return products.map(product => ({
      value: product.id,
      label: `${product.name || product.product_name || `Product #${product.id}`}${product.sku ? ` (${product.sku})` : ''}`
    }))
  }, [products])

  const categoryOptions = useMemo(() => {
    return categories.map(category => ({
      value: category.id,
      label: category.name
    }))
  }, [categories])

  const branchOptions = useMemo(() => {
    return branches.map(branch => ({
      value: branch.id,
      label: branch.name
    }))
  }, [branches])

  // Get selected values for react-select
  const selectedProduct = useMemo(() => {
    return formData.product 
      ? productOptions.find(opt => opt.value === parseInt(formData.product))
      : null
  }, [formData.product, productOptions])

  const selectedCategory = useMemo(() => {
    return formData.category 
      ? categoryOptions.find(opt => opt.value === parseInt(formData.category))
      : null
  }, [formData.category, categoryOptions])

  const selectedBranch = useMemo(() => {
    return formData.branch 
      ? branchOptions.find(opt => opt.value === parseInt(formData.branch))
      : null
  }, [formData.branch, branchOptions])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      // Validate discount value
      const discountValue = parseFloat(formData.discount_value)
      if (isNaN(discountValue) || discountValue <= 0) {
        Swal.fire({
          icon: 'error',
          title: 'Invalid Value',
          text: 'Discount value must be a positive number.'
        })
        setLoading(false)
        return
      }

      if (formData.discount_type === 'percentage' && discountValue > 100) {
        Swal.fire({
          icon: 'error',
          title: 'Invalid Value',
          text: 'Percentage discount cannot exceed 100%.'
        })
        setLoading(false)
        return
      }

      // Validate apply_to specific fields
      if (formData.apply_to === 'product' && !formData.product) {
        Swal.fire({
          icon: 'error',
          title: 'Validation Error',
          text: 'Please select a product when apply_to is "product".'
        })
        setLoading(false)
        return
      }

      if (formData.apply_to === 'category' && !formData.category) {
        Swal.fire({
          icon: 'error',
          title: 'Validation Error',
          text: 'Please select a category when apply_to is "category".'
        })
        setLoading(false)
        return
      }

      if (formData.apply_to === 'branch' && !formData.branch) {
        Swal.fire({
          icon: 'error',
          title: 'Validation Error',
          text: 'Please select a branch when apply_to is "branch".'
        })
        setLoading(false)
        return
      }

      if (formData.apply_to === 'min_purchase' && !formData.min_purchase_amount) {
        Swal.fire({
          icon: 'error',
          title: 'Validation Error',
          text: 'Please enter minimum purchase amount when apply_to is "min_purchase".'
        })
        setLoading(false)
        return
      }

      // Validate dates
      if (formData.start_date && formData.end_date) {
        const startDate = new Date(formData.start_date)
        const endDate = new Date(formData.end_date)
        if (endDate < startDate) {
          Swal.fire({
            icon: 'error',
            title: 'Invalid Dates',
            text: 'End date must be after start date.'
          })
          setLoading(false)
          return
        }
      }

      // Prepare payload according to backend API structure
      const payload = {
        name: formData.name.trim(),
        discount_type: formData.discount_type,
        discount_value: discountValue.toString(),
        apply_to: formData.apply_to,
        is_active: formData.is_active,
        description: formData.description.trim() || ''
      }

      // Add optional code
      if (formData.code && formData.code.trim()) {
        payload.code = formData.code.trim()
      }

      // Add apply_to specific fields
      if (formData.apply_to === 'product' && formData.product) {
        payload.product = parseInt(formData.product)
      }
      if (formData.apply_to === 'category' && formData.category) {
        payload.category = parseInt(formData.category)
      }
      if (formData.apply_to === 'branch' && formData.branch) {
        payload.branch = parseInt(formData.branch)
      }
      if (formData.apply_to === 'min_purchase' && formData.min_purchase_amount) {
        payload.min_purchase_amount = parseFloat(formData.min_purchase_amount).toString()
      }

      // Add optional fields
      if (formData.max_discount_amount && formData.max_discount_amount.trim()) {
        payload.max_discount_amount = parseFloat(formData.max_discount_amount).toString()
      }
      if (formData.usage_limit && formData.usage_limit.trim()) {
        payload.usage_limit = parseInt(formData.usage_limit)
      }

      // Format dates to ISO 8601 with timezone (UTC)
      // datetime-local input gives us a string like "2024-01-01T00:00" (local time)
      // We need to convert it to ISO 8601 format with UTC timezone
      if (formData.start_date) {
        // datetime-local gives local time, convert to ISO string (UTC)
        const startDate = new Date(formData.start_date)
        payload.start_date = startDate.toISOString()
      }
      if (formData.end_date) {
        // datetime-local gives local time, convert to ISO string (UTC)
        const endDate = new Date(formData.end_date)
        payload.end_date = endDate.toISOString()
      }

      // Use the correct endpoint
      const DISCOUNTS_ENDPOINT = `${BASE_URL}/api/sales/discounts/`
      await apiClient.post(DISCOUNTS_ENDPOINT, payload)

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Discount created successfully!',
        confirmButtonText: 'OK'
      })

      // Reset form
      setFormData({
        name: '',
        code: '',
        discount_type: 'percentage',
        discount_value: '',
        apply_to: 'all',
        product: '',
        category: '',
        branch: '',
        min_purchase_amount: '',
        max_discount_amount: '',
        start_date: '',
        end_date: '',
        usage_limit: '',
        is_active: true,
        description: ''
      })
    } catch (error) {
      console.error('Error creating discount:', error)
      const errorMessage = error.response?.data?.name?.[0] || 
                          error.response?.data?.discount_value?.[0] ||
                          error.response?.data?.code?.[0] ||
                          error.response?.data?.non_field_errors?.[0] ||
                          error.response?.data?.detail ||
                          error.response?.data?.message ||
                          'Failed to create discount. Please try again.'
      
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMessage
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <div className="pagetitle">
        <h1>Set Discounts</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item">Sale</li>
            <li className="breadcrumb-item active">Set Discounts</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">
                  <i className="bi bi-percent me-2"></i>
                  Create New Discount
                </h5>

                <form onSubmit={handleSubmit}>
                  <div className="row mb-3">
                    <label htmlFor="name" className="col-sm-2 col-form-label">Discount Name <span className="text-danger">*</span></label>
                    <div className="col-sm-10">
                      <input
                        type="text"
                        className="form-control"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Enter discount name (e.g., Summer Sale, Black Friday)"
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <div className="row mb-3">
                    <label htmlFor="discount_type" className="col-sm-2 col-form-label">Discount Type <span className="text-danger">*</span></label>
                    <div className="col-sm-10">
                      <select
                        className="form-select"
                        id="discount_type"
                        name="discount_type"
                        value={formData.discount_type}
                        onChange={handleChange}
                        required
                        disabled={loading}
                      >
                        <option value="percentage">Percentage (%)</option>
                        <option value="fixed">Fixed Amount</option>
                      </select>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <label htmlFor="code" className="col-sm-2 col-form-label">Discount Code</label>
                    <div className="col-sm-10">
                      <input
                        type="text"
                        className="form-control"
                        id="code"
                        name="code"
                        value={formData.code}
                        onChange={handleChange}
                        placeholder="Enter discount code (e.g., OPENING30) - Optional"
                        disabled={loading}
                      />
                      <small className="text-muted">Leave empty if no code is needed</small>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <label htmlFor="discount_value" className="col-sm-2 col-form-label">
                      Discount Value <span className="text-danger">*</span>
                      {formData.discount_type === 'percentage' && <small className="text-muted d-block">(0-100%)</small>}
                    </label>
                    <div className="col-sm-10">
                      <div className="input-group">
                        <input
                          type="number"
                          className="form-control"
                          id="discount_value"
                          name="discount_value"
                          value={formData.discount_value}
                          onChange={handleChange}
                          required
                          min="0"
                          max={formData.discount_type === 'percentage' ? '100' : undefined}
                          step="0.01"
                          placeholder={formData.discount_type === 'percentage' ? 'Enter percentage (e.g., 10 for 10%)' : 'Enter fixed amount'}
                          disabled={loading}
                        />
                        <span className="input-group-text">
                          {formData.discount_type === 'percentage' ? '%' : 'Amount'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <label htmlFor="apply_to" className="col-sm-2 col-form-label">Apply To <span className="text-danger">*</span></label>
                    <div className="col-sm-10">
                      <select
                        className="form-select"
                        id="apply_to"
                        name="apply_to"
                        value={formData.apply_to}
                        onChange={handleChange}
                        required
                        disabled={loading}
                      >
                        <option value="all">All Products</option>
                        <option value="product">Specific Product</option>
                        <option value="category">Product Category</option>
                        <option value="branch">Specific Branch</option>
                        <option value="min_purchase">Minimum Purchase Amount</option>
                      </select>
                    </div>
                  </div>

                  {formData.apply_to === 'product' && (
                    <div className="row mb-3">
                      <label htmlFor="product" className="col-sm-2 col-form-label">Select Product <span className="text-danger">*</span></label>
                      <div className="col-sm-10">
                        <Select
                          value={selectedProduct}
                          onChange={(option) => {
                            setFormData({
                              ...formData,
                              product: option ? option.value.toString() : ''
                            })
                          }}
                          options={productOptions}
                          isClearable
                          isSearchable
                          placeholder="Search and select a product..."
                          isDisabled={loading}
                          required
                          styles={{
                            control: (base) => ({
                              ...base,
                              minHeight: '38px',
                              borderColor: '#ced4da',
                              '&:hover': {
                                borderColor: '#ced4da'
                              }
                            })
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {formData.apply_to === 'category' && (
                    <div className="row mb-3">
                      <label htmlFor="category" className="col-sm-2 col-form-label">Select Category <span className="text-danger">*</span></label>
                      <div className="col-sm-10">
                        <Select
                          value={selectedCategory}
                          onChange={(option) => {
                            setFormData({
                              ...formData,
                              category: option ? option.value.toString() : ''
                            })
                          }}
                          options={categoryOptions}
                          isClearable
                          isSearchable
                          placeholder="Search and select a category..."
                          isDisabled={loading}
                          required
                          styles={{
                            control: (base) => ({
                              ...base,
                              minHeight: '38px',
                              borderColor: '#ced4da',
                              '&:hover': {
                                borderColor: '#ced4da'
                              }
                            })
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {formData.apply_to === 'branch' && (
                    <div className="row mb-3">
                      <label htmlFor="branch" className="col-sm-2 col-form-label">Select Branch <span className="text-danger">*</span></label>
                      <div className="col-sm-10">
                        <Select
                          value={selectedBranch}
                          onChange={(option) => {
                            setFormData({
                              ...formData,
                              branch: option ? option.value.toString() : ''
                            })
                          }}
                          options={branchOptions}
                          isClearable
                          isSearchable
                          placeholder="Search and select a branch..."
                          isDisabled={loading}
                          required
                          styles={{
                            control: (base) => ({
                              ...base,
                              minHeight: '38px',
                              borderColor: '#ced4da',
                              '&:hover': {
                                borderColor: '#ced4da'
                              }
                            })
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {formData.apply_to === 'min_purchase' && (
                    <div className="row mb-3">
                      <label htmlFor="min_purchase_amount" className="col-sm-2 col-form-label">Minimum Purchase Amount <span className="text-danger">*</span></label>
                      <div className="col-sm-10">
                        <div className="input-group">
                          <span className="input-group-text">$</span>
                          <input
                            type="number"
                            className="form-control"
                            id="min_purchase_amount"
                            name="min_purchase_amount"
                            value={formData.min_purchase_amount}
                            onChange={handleChange}
                            required
                            min="0"
                            step="0.01"
                            placeholder="Enter minimum purchase amount"
                            disabled={loading}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {formData.discount_type === 'percentage' && (
                    <div className="row mb-3">
                      <label htmlFor="max_discount_amount" className="col-sm-2 col-form-label">Maximum Discount Amount</label>
                      <div className="col-sm-10">
                        <div className="input-group">
                          <span className="input-group-text">$</span>
                          <input
                            type="number"
                            className="form-control"
                            id="max_discount_amount"
                            name="max_discount_amount"
                            value={formData.max_discount_amount}
                            onChange={handleChange}
                            min="0"
                            step="0.01"
                            placeholder="Enter maximum discount amount (optional)"
                            disabled={loading}
                          />
                        </div>
                        <small className="text-muted">Caps the discount amount for percentage discounts</small>
                      </div>
                    </div>
                  )}

                  <div className="row mb-3">
                    <label htmlFor="usage_limit" className="col-sm-2 col-form-label">Usage Limit</label>
                    <div className="col-sm-10">
                      <input
                        type="number"
                        className="form-control"
                        id="usage_limit"
                        name="usage_limit"
                        value={formData.usage_limit}
                        onChange={handleChange}
                        min="1"
                        step="1"
                        placeholder="Enter maximum number of times this discount can be used (optional)"
                        disabled={loading}
                      />
                      <small className="text-muted">Leave empty for unlimited usage</small>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <label htmlFor="start_date" className="col-sm-2 col-form-label">Start Date</label>
                    <div className="col-sm-10">
                      <input
                        type="datetime-local"
                        className="form-control"
                        id="start_date"
                        name="start_date"
                        value={formData.start_date}
                        onChange={handleChange}
                        disabled={loading}
                      />
                      <small className="text-muted">Leave empty for immediate start</small>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <label htmlFor="end_date" className="col-sm-2 col-form-label">End Date</label>
                    <div className="col-sm-10">
                      <input
                        type="datetime-local"
                        className="form-control"
                        id="end_date"
                        name="end_date"
                        value={formData.end_date}
                        onChange={handleChange}
                        disabled={loading}
                      />
                      <small className="text-muted">Leave empty for no expiration</small>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <label htmlFor="description" className="col-sm-2 col-form-label">Description</label>
                    <div className="col-sm-10">
                      <textarea
                        className="form-control"
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        rows="3"
                        placeholder="Enter discount description (optional)"
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-sm-10 offset-sm-2">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="is_active"
                          name="is_active"
                          checked={formData.is_active}
                          onChange={handleChange}
                          disabled={loading}
                        />
                        <label className="form-check-label" htmlFor="is_active">
                          Active (Enable this discount immediately)
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-sm-10 offset-sm-2">
                      <button 
                        type="submit" 
                        className="btn btn-primary"
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Creating...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-plus-circle me-2"></i>
                            Create Discount
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default SetDiscounts

